import { MediaQuery } from '@cae/cobalt-react/enums';
import { useMediaQuery } from '@cae/cobalt-react/hooks';
import { trackHeapEvent } from '@configs/heap';
import { useOrganizations } from '@contexts/organizations-provider';
import { usePermissions } from '@contexts/permissions-provider';
import { TilesCarousel } from '@shared/components/tiles-carousel/TilesCarousel';
import { useFlags } from 'launchdarkly-react-client-sdk';
import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import type { QuickLinkId } from './QuickLinks.types';

import { QuickLinkTile } from './quick-link-tile/QuickLinkTile';
import styles from './QuickLinks.module.css';
import { QUICK_LINK_HEAP_EVENTS, QUICK_LINKS } from './QuickLinks.utils';

const QuickLinks = React.memo((): React.ReactElement => {
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedOrganization } = useOrganizations();
  const isMobileAndTablet = useMediaQuery(MediaQuery.MobileAndTablet);
  const flags = useFlags();
  const isPart135 = selectedOrganization?.isPart135CertificateCode ?? false;

  const visibleLinks = React.useMemo(
    () =>
      QUICK_LINKS.filter(
        link =>
          link.permissions.some(p => hasPermission(p)) &&
          (link.featureFlag === undefined ||
            flags[link.featureFlag] === true) &&
          (link.requiresPart135 !== true || isPart135)
      ),
    [hasPermission, flags, isPart135]
  );

  const handleTileClick = React.useCallback(
    (linkId: QuickLinkId, route: string): void => {
      const customerId = selectedOrganization?.customerId;
      if (!customerId) return;

      const heapEvent = QUICK_LINK_HEAP_EVENTS[linkId];
      if (heapEvent) {
        trackHeapEvent(heapEvent.eventName, {
          elementId: heapEvent.elementId,
          source: 'host',
          appArea: 'manage',
          route: location.pathname,
        });
      }

      navigate(route.replace(':customerId', customerId));
    },
    [location.pathname, navigate, selectedOrganization]
  );

  const tiles = React.useMemo(
    () =>
      visibleLinks.map(link => (
        <QuickLinkTile
          key={link.id}
          label={link.label}
          icon={link.icon}
          onClick={() => handleTileClick(link.id, link.route)}
        />
      )),
    [visibleLinks, handleTileClick]
  );

  if (visibleLinks.length === 0) return <></>;

  return (
    <section
      className={styles.quickLinks}
      data-testid="quick-links"
      aria-labelledby="quick-links-title"
    >
      <h2 className="heading-02" id="quick-links-title">
        What are your goals for today?
      </h2>
      {isMobileAndTablet ? (
        <TilesCarousel>{tiles}</TilesCarousel>
      ) : (
        <div className={styles.quickLinksGrid}>{tiles}</div>
      )}
    </section>
  );
});

QuickLinks.displayName = 'QuickLinks';

export { QuickLinks };
